import React from "react";
import "../TncPrivacyPolicy/TNC.css";
import { Container } from "react-bootstrap";
import {
  FaFileContract,
  FaDownload,
  FaMusic,
  FaCopyright,
  FaUserShield,
  FaBan,
  FaMoneyBillWave,
  FaGavel,
  FaShieldAlt,
  FaSyncAlt,
  FaGlobe,
  FaExclamationTriangle,
  FaBalanceScale,
  FaHandshake,
  FaEnvelope,
  FaPhoneAlt,
} from "react-icons/fa";

const ArtistAgreement = () => {
  return (
    <div className="tnc-container">
      <Container>
        <div className="tnc-wrapper">
          {/* Header Section */}
          <div className="tnc-header">
            <h1 className="main-title">
              <span className="text-yellow">MUSIC DISTRIBUTION</span>{" "}
              <span className="text-pink">AGREEMENT</span>
            </h1>
            
            <p className="intro-text">
              This Music Distribution Agreement ("Agreement") governs the relationship between the music distribution service provider ("Distributor") and any individual, artist, band, record label, rights holder, or entity ("Artist") that submits content for digital distribution through the Distributor's platform.
            </p>
            <p className="intro-text mt-3">
              By submitting content for distribution, the Artist acknowledges that they have read, understood, and agree to be bound by the terms and conditions set forth in this Agreement.
            </p>

            <div className="mt-4 flex justify-content-center">
              <a
                href="/Images/artist-agreement.pdf"
                download="artist-agreement.pdf"
                className="btn btn-primary"
                style={{
                  background: "linear-gradient(45deg, #d70d57, #ff4081)",
                  border: "none",
                  padding: "10px 25px",
                  borderRadius: "25px",
                  color: "#fff",
                  fontWeight: "bold",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px"
                }}
              >
                <FaDownload /> Download PDF
              </a>
            </div>
          </div>

          {/* Section 1 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number purple-gradient">1</span>
              <FaFileContract className="section-icon-inline mr-2 text-purple" />{" "}
              DEFINITIONS
            </h2>
            <p className="section-text mb-2">For the purposes of this Agreement:</p>
            <ul className="custom-list mb-3">
              <li><strong>"Content"</strong> means any sound recording, musical composition, album, single, EP, music video, artwork, metadata, lyrics, audiovisual material, or other media submitted by the Artist.</li>
              <li><strong>"Digital Service Providers" ("DSPs")</strong> means third-party platforms including but not limited to Spotify, Apple Music, YouTube Music, Amazon Music, Deezer, TIDAL, TikTok, Instagram, Facebook, JioSaavn, Gaana, Wynk, and similar services.</li>
              <li><strong>"Distribution Services"</strong> means the delivery, management, monetization, promotion, administration, and maintenance of Content on DSPs.</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number orange-gradient">2</span>
              <FaHandshake className="section-icon-inline mr-2 text-orange" />{" "}
              APPOINTMENT OF DISTRIBUTOR
            </h2>
            <p className="section-text mb-3">
              The Artist hereby appoints the Distributor on a non-exclusive basis to distribute, make available, market, monetize, administer, and otherwise exploit the Content through DSPs and other authorized distribution channels worldwide.
            </p>
            <p className="section-text mb-3">
              The Distributor may sublicense, transmit, encode, store, reproduce, publish, and deliver the Content solely for the purpose of providing Distribution Services.
            </p>
            <p className="section-text">
              Nothing contained herein shall transfer ownership of the Content to the Distributor.
            </p>
          </div>

          {/* Section 3 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number pink-gradient">3</span>
              <FaCopyright className="section-icon-inline mr-2 text-pink" />{" "}
              OWNERSHIP OF RIGHTS
            </h2>
            <p className="section-text mb-3">
              The Artist shall retain all ownership rights, title, and interest in and to the Content, including all copyrights, neighboring rights, publishing rights, trademarks, and master recording rights.
            </p>
            <p className="section-text">
              Except for the limited rights granted under this Agreement, no ownership interest is assigned, transferred, or conveyed to the Distributor.
            </p>
          </div>

          {/* Section 4 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number blue-gradient">4</span>
              <FaUserShield className="section-icon-inline mr-2 text-blue" />{" "}
              ARTIST WARRANTIES AND REPRESENTATIONS
            </h2>
            <p className="section-text mb-2">The Artist represents and warrants that:</p>
            <ol className="custom-list mb-3" style={{ listStyleType: "decimal", paddingLeft: "1.5rem" }}>
              <li>The Artist owns or controls all rights necessary to distribute the Content.</li>
              <li>The Content does not infringe upon any copyright, trademark, patent, trade secret, publicity right, privacy right, or other proprietary right of any third party.</li>
              <li>All samples, compositions, lyrics, artwork, recordings, performances, and audiovisual elements have been properly licensed and authorized.</li>
              <li>The Content does not contain unlawful, fraudulent, defamatory, misleading, obscene, hateful, discriminatory, or otherwise prohibited material.</li>
              <li>All metadata, credits, ownership information, and royalty information supplied to the Distributor are accurate and complete.</li>
              <li>The Artist has full legal authority to enter into this Agreement.</li>
              <li>The Artist is solely responsible for obtaining all licenses, permissions, consents, and clearances required for the Content.</li>
            </ol>
          </div>

          {/* Section 5 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number red-gradient">5</span>
              <FaBan className="section-icon-inline mr-2 text-red" />{" "}
              CONTENT REVIEW AND APPROVAL
            </h2>
            <p className="section-text mb-2">
              The Distributor reserves the right, at its sole discretion, to review, reject, suspend, remove, or refuse to distribute any Content that:
            </p>
            <ul className="custom-list red-bullets mb-3">
              <li>Violates applicable laws or regulations;</li>
              <li>Violates DSP policies or guidelines;</li>
              <li>Is subject to copyright disputes or ownership claims;</li>
              <li>Contains fraudulent metadata or misleading information;</li>
              <li>Creates legal, reputational, or commercial risk for the Distributor.</li>
            </ul>
            <p className="section-text">
              The Distributor shall have no obligation to provide reasons for rejecting any Content submission.
            </p>
          </div>

          {/* Section 6 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number green-gradient">6</span>
              <FaMusic className="section-icon-inline mr-2 text-green" />{" "}
              DISTRIBUTION SERVICES
            </h2>
            <p className="section-text mb-2">The Distributor shall use commercially reasonable efforts to:</p>
            <ul className="custom-list mb-3">
              <li>Deliver Content to DSPs and partner platforms;</li>
              <li>Manage content ingestion and metadata processing;</li>
              <li>Facilitate royalty collection where applicable;</li>
              <li>Maintain content availability subject to DSP policies;</li>
              <li>Process takedown requests and updates when reasonably possible.</li>
            </ul>
            <p className="section-text mb-3">
              The Artist acknowledges that DSPs maintain independent policies and may reject, remove, modify, monetize, demonetize, or otherwise manage Content at their discretion.
            </p>
            <p className="section-text">
              The Distributor cannot guarantee placement, playlist inclusion, promotion, availability, or revenue generation.
            </p>
          </div>

          {/* Section 7 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number orange-gradient">7</span>
              <FaMoneyBillWave className="section-icon-inline mr-2 text-orange" />{" "}
              ROYALTIES AND PAYMENTS
            </h2>
            <p className="section-text mb-3">
              Royalties shall be calculated based solely on reports and payments received from DSPs and other authorized revenue sources.
            </p>
            <p className="section-text mb-3">
              The Distributor shall remit royalties to the Artist in accordance with the applicable revenue-sharing arrangement and payment policies published by the Distributor.
            </p>
            <p className="section-text mb-2">The Distributor reserves the right to:</p>
            <ul className="custom-list mb-3">
              <li>Withhold payments pending investigation of suspected fraud;</li>
              <li>Offset chargebacks, reversals, penalties, or overpayments;</li>
              <li>Delay payments while ownership disputes remain unresolved.</li>
            </ul>
            <p className="section-text">
              No royalties shall be due until funds are actually received by the Distributor.
            </p>
          </div>

          {/* Section 8 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number pink-gradient">8</span>
              <FaGavel className="section-icon-inline mr-2 text-pink" />{" "}
              COPYRIGHT CLAIMS AND INFRINGEMENT
            </h2>
            <p className="section-text mb-3">
              The Artist shall be solely responsible for resolving any copyright, ownership, licensing, royalty, or rights disputes relating to the Content.
            </p>
            <p className="section-text mb-3">
              If the Distributor receives any complaint, takedown notice, copyright claim, legal demand, or allegation concerning the Content, the Distributor may immediately suspend or remove the Content without prior notice.
            </p>
            <p className="section-text">
              The Distributor shall not be responsible for investigating ownership claims beyond reasonable administrative review.
            </p>
          </div>

          {/* Section 9 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number purple-gradient">9</span>
              <FaShieldAlt className="section-icon-inline mr-2 text-purple" />{" "}
              INDEMNIFICATION
            </h2>
            <p className="section-text mb-2">
              The Artist agrees to indemnify, defend, and hold harmless the Distributor, its affiliates, officers, directors, employees, contractors, partners, successors, and assigns from and against any claims, liabilities, damages, judgments, penalties, losses, costs, and expenses, including reasonable legal fees, arising out of:
            </p>
            <ul className="custom-list mb-0">
              <li>The Artist's breach of this Agreement;</li>
              <li>Any claim relating to ownership of the Content;</li>
              <li>Alleged copyright infringement;</li>
              <li>Misrepresentation of rights or permissions;</li>
              <li>Violation of any law, regulation, or third-party right.</li>
            </ul>
          </div>

          {/* Section 10 */}
          <div className="policy-section card-gradient-border">
            <h2 className="section-title">
              <span className="section-number red-gradient">10</span>
              <FaBan className="section-icon-inline mr-2 text-red" />{" "}
              TERM AND TERMINATION
            </h2>
            <p className="section-text mb-3">This Agreement shall remain effective until terminated.</p>
            <p className="section-text mb-3">The Artist may request removal of Content by submitting written notice to the Distributor.</p>
            <p className="section-text mb-2">The Distributor may terminate this Agreement or remove Content immediately if:</p>
            <ul className="custom-list red-bullets mb-3">
              <li>The Artist breaches this Agreement;</li>
              <li>Fraudulent activity is detected;</li>
              <li>DSP policies are violated;</li>
              <li>Legal or copyright concerns arise;</li>
              <li>Continued distribution may expose the Distributor to liability.</li>
            </ul>
            <p className="section-text">
              The Distributor shall use reasonable efforts to process takedown requests; however, removal timelines are subject to DSP processing schedules.
            </p>
          </div>

          {/* Grid for 11 & 12 */}
          <div className="cards-grid-2">
            {/* Section 11 */}
            <div className="policy-section card-gradient-border">
              <h2 className="section-title">
                <span className="section-number blue-gradient">11</span>
                <FaExclamationTriangle className="section-icon-inline mr-2 text-blue" />{" "}
                LIMITATION OF LIABILITY
              </h2>
              <p className="section-text mb-2">To the maximum extent permitted by law, the Distributor shall not be liable for:</p>
              <ul className="custom-list mb-3">
                <li>Lost profits or revenue;</li>
                <li>Lost business opportunities;</li>
                <li>Loss of data or content;</li>
                <li>Delays in distribution;</li>
                <li>DSP actions or decisions;</li>
                <li>Service interruptions;</li>
                <li>Technical failures;</li>
                <li>Indirect, incidental, consequential, special, punitive, or exemplary damages.</li>
              </ul>
              <p className="section-text">
                The Distributor's total aggregate liability under this Agreement shall not exceed the total amount of royalties actually paid to the Artist during the twelve (12) months immediately preceding the event giving rise to the claim.
              </p>
            </div>

            {/* Section 12 */}
            <div className="policy-section card-gradient-border">
              <h2 className="section-title">
                <span className="section-number orange-gradient">12</span>
                <FaGlobe className="section-icon-inline mr-2 text-orange" />{" "}
                FORCE MAJEURE
              </h2>
              <p className="section-text">
                The Distributor shall not be liable for any delay or failure to perform resulting from causes beyond its reasonable control, including acts of God, natural disasters, governmental actions, internet outages, labor disputes, cyberattacks, platform failures, or other unforeseen events.
              </p>
            </div>
          </div>

          {/* Grid for 13, 14, 15 */}
          <div className="cards-grid-3">
            {/* Section 13 */}
            <div className="policy-section card-gradient-border d-flex-col">
              <h2 className="section-title">
                <span className="section-number pink-gradient">13</span>
                <FaHandshake className="section-icon-inline mr-2 text-pink" />{" "}
                INDEPENDENT CONTRACTOR
              </h2>
              <p className="section-text mb-3">
                Nothing contained in this Agreement shall create any partnership, employment, joint venture, agency, fiduciary, or similar relationship between the parties.
              </p>
              <p className="section-text">
                Each party acts solely as an independent contractor.
              </p>
            </div>

            {/* Section 14 */}
            <div className="policy-section card-gradient-border d-flex-col">
              <h2 className="section-title">
                <span className="section-number green-gradient">14</span>
                <FaBalanceScale className="section-icon-inline mr-2 text-green" />{" "}
                GOVERNING LAW
              </h2>
              <p className="section-text mb-3">
                This Agreement shall be governed by and construed in accordance with the laws of the jurisdiction in which the Distributor is established, without regard to conflict-of-law principles.
              </p>
              <p className="section-text">
                Any dispute arising out of or relating to this Agreement shall be subject to the exclusive jurisdiction of the competent courts of that jurisdiction.
              </p>
            </div>

            {/* Section 15 */}
            <div className="policy-section card-gradient-border d-flex-col">
              <h2 className="section-title">
                <span className="section-number purple-gradient">15</span>
                <FaSyncAlt className="section-icon-inline mr-2 text-purple" />{" "}
                ENTIRE AGREEMENT
              </h2>
              <p className="section-text mb-3">
                This Agreement constitutes the complete and exclusive agreement between the parties regarding the subject matter herein and supersedes all prior negotiations, representations, understandings, and agreements.
              </p>
              <p className="section-text">
                The Distributor may update this Agreement from time to time. Continued use of the Distribution Services following publication of an updated Agreement shall constitute acceptance of the revised terms.
              </p>
            </div>
          </div>

        </div>
      </Container>
    </div>
  );
};

export default ArtistAgreement;
